import { useState } from "react";
import { CheckIcon, MapPinIcon, PencilIcon } from "@heroicons/react/24/outline";
import { FACILITY } from "../../../../constants";
import type { OrderFormValues } from "../../../../types/forms";
import { useUpdateShippingStatus } from "../../../../hooks/useUpdateShippingStatus";
import AccountBenefits from "../accountBenefits";
import TrackingEditForm from "../trackingEditForm";

interface ConfirmedShippingDetailsProps {
  orderId: string;
  trackingInfo: OrderFormValues["trackingInfo"];
}

export default function ConfirmedShippingDetails({
  orderId,
  trackingInfo,
}: ConfirmedShippingDetailsProps) {
  const [isEditing, setIsEditing] = useState(false);
  const updateMutation = useUpdateShippingStatus(orderId, {
    onSuccess: () => setIsEditing(false),
  });

  return (
    <>
      <div className="bg-white rounded-2xl md:px-10 px-6 py-7 shadow-sm border border-slate-100 flex flex-col items-center w-full max-w-xl mx-auto">
        <h2 className="md:text-2xl text-xl font-medium text-slate-900 mb-8 text-center">
          Shipping details
        </h2>
        <div className="w-full space-y-4">
          <div className="bg-[#FBFDFF] border border-slate-100 rounded-2xl md:p-5 px-4 py-3 flex gap-4">
            <MapPinIcon className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
            <div className="flex flex-col gap-1">
              <h3 className="text-blue-500 md:text-xl text-sm font-medium">
                Your item has been sent to our facility at:
              </h3>
              <p className="text-slate-500 md:text-xl text-xs font-medium leading-snug">
                {FACILITY.name}
              </p>
              <p className="text-slate-500 md:text-xl text-xs font-normal leading-snug">
                {FACILITY.address}
              </p>
            </div>
          </div>

          <div className="bg-[#FBFDFF] border border-slate-100 rounded-2xl md:p-5 px-4 py-3 flex gap-4">
            <CheckIcon className="w-6 h-6 text-blue-500 shrink-0 mt-0.5 font-bold" />
            <div className="w-full flex flex-col gap-1">
              <h3 className="text-blue-500 md:text-xl text-sm font-medium mb-3">
                Shipment confirmed
              </h3>

              {!isEditing ? (
                <div className="flex flex-col gap-4">
                  <div>
                    <p className="text-slate-400 md:text-xl text-xs tracking-wider font-normal mb-1">
                      Tracking ID
                    </p>
                    <div className="flex items-center">
                      <span className="font-normal text-slate-600 md:text-xl text-xs break-all">
                        {trackingInfo.trackingNumber}
                      </span>
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="text-slate-600 hover:text-slate-500 md:ml-4 ml-2 cursor-pointer transition-colors"
                      >
                        <PencilIcon className="md:w-5 md:h-5 w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-slate-400 md:text-xl text-xs tracking-wider font-normal">
                      Carrier
                    </p>
                    <span className="font-normal text-slate-600 md:text-xl text-xs">
                      {trackingInfo.carrier}
                    </span>
                  </div>
                </div>
              ) : (
                <TrackingEditForm
                  defaultTrackingNumber={trackingInfo.trackingNumber}
                  defaultCarrier={trackingInfo.carrier}
                  isSaving={updateMutation.isPending}
                  onCancel={() => setIsEditing(false)}
                  onSave={(updatedTrackingInfo) =>
                    updateMutation.mutate({
                      trackingInfo: updatedTrackingInfo,
                      trackingImage: null,
                    })
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="bg-white rounded-2xl md:px-10 px-6 py-7 shadow-sm border border-slate-100 w-full max-w-xl mx-auto mt-4 auth-nav-toggle">
        <AccountBenefits
          title="Want to track your service progress?"
          subtitle="Create a free account for a seamless service experience."
          benefits={[
            "Get shipping labels quickly",
            "Track your repair in real time",
            "Receive status updates by email",
            "View all your orders in one place",
          ]}
        />
      </div>
    </>
  );
}
